<script setup lang="ts">
/**
 * Site header and primary navigation.
 *
 * The `<nav>` elements carry `aria-label`s because the footer has one too — several unlabelled
 * navigation landmarks are indistinguishable to anyone navigating by landmark. `aria-current="page"`
 * comes from `router-link`'s active class handling and is what tells a screen reader which link
 * represents the current page.
 *
 * The mobile panel is a disclosure rather than a modal dialog: it pushes nothing, traps nothing, and
 * needs no focus management, which is the whole reason to prefer it. What it does need is the
 * `aria-expanded`/`aria-controls` pair, an Escape handler, and to close itself on navigation —
 * leaving it open over the page it just navigated to is the classic version of this bug.
 */

/**
 * Vue
 */
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { Menu, X } from '@lucide/vue'
import { onKeyStroke, useWindowScroll } from '@vueuse/core'

/**
 * Components
 */
import LanguageToggle from '@/components/LanguageToggle.vue'
import ThemeToggle from '@/components/ThemeToggle.vue'
import AppContainer from '@/components/ui/AppContainer.vue'

/**
 * Config
 */
import { siteConfig } from '@/config/site'

const { t } = useI18n()
const route = useRoute()

/*
 * The header is transparent over the hero and only grows a border and a blur once the page has
 * moved. A permanent border draws a hard line across the aurora backdrop before there is anything
 * above it to separate.
 */
const { y } = useWindowScroll()

const open = ref(false)

watch(
  () => route.fullPath,
  () => {
    open.value = false
  }
)

onKeyStroke('Escape', () => {
  open.value = false
})

const links = [
  { to: '/', key: 'nav.home' },
  { to: '/about', key: 'nav.about' },
  { to: '/author', key: 'nav.author' },
  { to: '/documentation', key: 'nav.documentation' },
  { to: '/contact', key: 'nav.contact' }
]
</script>

<template>
  <header
    class="sticky top-0 z-40 transition duration-300"
    :class="y > 8 || open ? 'border-b border-border bg-background/80 backdrop-blur-md' : 'border-b border-transparent'"
  >
    <AppContainer class="flex h-[72px] items-center justify-between gap-4">
      <router-link to="/" class="group flex items-center gap-2.5">
        <!--
          The mark is the site initial, not an image: one less request on the critical path, and it
          cannot go stale against `siteConfig.name`. Decorative, because the name beside it already
          names the link.
        -->
        <span
          class="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-chart-2 font-headline text-sm font-bold text-primary-foreground shadow-sm shadow-primary/25 transition group-hover:scale-105"
          aria-hidden="true"
        >
          {{ siteConfig.shortName.slice(0, 2) }}
        </span>
        <span class="font-headline text-base font-bold tracking-tight">{{ siteConfig.name }}</span>
      </router-link>

      <nav :aria-label="siteConfig.name" class="hidden items-center gap-1 md:flex">
        <router-link
          v-for="link in links"
          :key="link.to"
          :to="link.to"
          class="rounded-lg px-3.5 py-2 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
          active-class="bg-accent text-foreground"
          exact-active-class="bg-accent text-foreground"
        >
          {{ t(link.key) }}
        </router-link>
      </nav>

      <div class="flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />

        <button
          type="button"
          class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition hover:bg-accent md:hidden"
          :aria-label="open ? t('actions.closeMenu') : t('actions.openMenu')"
          :aria-expanded="open"
          aria-controls="mobile-nav"
          @click="open = !open"
        >
          <X v-if="open" class="h-4 w-4" aria-hidden="true" />
          <Menu v-else class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </AppContainer>

    <!--
      Kept in the DOM and hidden with `v-show`, so the links are in the prerendered HTML whether or
      not the panel has been opened. Rendered with `v-if` they would exist only after a click, which
      is a navigation a crawler never sees.
    -->
    <div v-show="open" id="mobile-nav" class="border-t border-border bg-background/95 backdrop-blur-md md:hidden">
      <AppContainer class="py-3">
        <nav :aria-label="siteConfig.name + ' mobile'" class="flex flex-col">
          <router-link
            v-for="link in links"
            :key="link.to"
            :to="link.to"
            class="rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
            active-class="text-foreground"
            exact-active-class="text-foreground"
          >
            {{ t(link.key) }}
          </router-link>
        </nav>
      </AppContainer>
    </div>
  </header>
</template>
